import { Frequency, WRAPPER_VALUES, wrapperLabel } from '@cf/shared';
import { taxYearContaining } from './tax-year.js';
/**
 * Thrown at an OUTPUT boundary when a plan carrying a blocking finding is
 * about to become something an adviser or client would read. The findings
 * travel with the error so the API can render each against its own field.
 */
export class PlanRefusedError extends Error {
    findings;
    constructor(findings) {
        const blocking = findings.filter((f) => f.severity === 'blocking');
        super(`This plan cannot be projected as entered: ${blocking.map((f) => f.message).join(' ')}`.trim());
        this.name = 'PlanRefusedError';
        this.findings = findings;
    }
}
/**
 * Income that can only arise from an asset the household holds. Earnings, a
 * state pension and a DB pension are promises from an employer or the state,
 * not yields on a pot, so they are NOT evidence of a missing balance sheet —
 * a client with a salary and no savings yet is an ordinary, valid plan.
 */
const ASSET_DERIVED_INCOME = new Set([
    'rental',
    'dividend',
    'savings_interest',
]);
/** Planned-event kinds that expand into living-cost spending. */
const SPENDING_EVENT_KINDS = new Set([
    'schoolFees',
    'university',
    'care',
]);
function isSpendingEvent(event) {
    if (SPENDING_EVENT_KINDS.has(event.kind))
        return true;
    return event.kind === 'recurring' && (event.direction ?? 'outflow') === 'outflow';
}
/** Annualised amount, so a monthly and an annual line compare like for like. */
function annualised(amount, frequency) {
    switch (frequency) {
        case Frequency.Monthly:
            return amount * 12;
        case Frequency.Quarterly:
            return amount * 4;
        default:
            return amount;
    }
}
/**
 * How close a stream must be to the total of the others to read as a summary
 * row rather than a coincidence: 1% either way, per the brief.
 */
const DUPLICATE_TOTAL_TOLERANCE_BPS = 100;
/**
 * A "gross total" row summarises a TABLE, so the pattern only makes sense with
 * several detail rows beneath it. Below this many other streams the arithmetic
 * is too easily a coincidence — two partners on the same salary would match
 * exactly — and a false refusal stops an adviser working on a correct plan.
 */
const MIN_STREAMS_BEHIND_A_TOTAL = 3;
// ---- warning helpers --------------------------------------------------------
/** Wrappers an asset in this plan actually uses (so unused ones stay quiet). */
function wrappersInUse(assets) {
    return new Set(assets.filter((a) => a.currentValue > 0 || a.contribution > 0).map((a) => a.wrapper));
}
/** Every escalation attached anywhere in the plan, for the consumed-check. */
function escalationsIn(input) {
    const out = [];
    for (const s of input.spending)
        if (s.escalation !== undefined)
            out.push(s.escalation);
    for (const a of input.assets) {
        if (a.contributionEscalation !== undefined)
            out.push(a.contributionEscalation);
    }
    for (const e of input.plannedEvents ?? []) {
        if ('escalation' in e && e.escalation !== undefined)
            out.push(e.escalation);
    }
    return out;
}
/** Escalation kinds present, flattening `stepped` into its steps' kinds. */
function escalationKinds(escalations) {
    const kinds = new Set();
    const visit = (esc) => {
        kinds.add(esc.kind);
        if (esc.kind === 'stepped')
            for (const step of esc.steps)
                visit(step.rate);
    };
    for (const esc of escalations)
        visit(esc);
    return kinds;
}
/** Custom index names referenced by an `indexPlus` escalation anywhere. */
function referencedIndices(escalations) {
    const names = new Set();
    const visit = (esc) => {
        if (esc.kind === 'indexPlus')
            names.add(esc.index);
        if (esc.kind === 'stepped')
            for (const step of esc.steps)
                visit(step.rate);
    };
    for (const esc of escalations)
        visit(esc);
    return names;
}
/**
 * Which assumption keys THIS plan can possibly consume. Publishing a rate in
 * the assumptions table that no calculation read is a compliance defect in its
 * own right: the reader is told the plan was built on a figure it was not.
 *
 * Derived declaratively from the inputs rather than by instrumenting every
 * read inside the engine — same answer, no hot-path cost, and it cannot drift
 * out of step with a code path that silently stops running.
 */
function consumedAssumptionKeys(input) {
    const consumed = new Set();
    const streams = input.incomeStreams;
    const escalations = escalationsIn(input);
    const kinds = escalationKinds(escalations);
    const wrappers = wrappersInUse(input.assets);
    // CPI drives inflation-linked spending, the default escalation on most
    // income streams, planned-event pricing and every `cpiPlus` escalation.
    if (input.spending.some((s) => s.inflationLinked) ||
        streams.some((s) => s.growthRateBps === null) ||
        (input.plannedEvents ?? []).length > 0 ||
        kinds.has('cpiPlus')) {
        consumed.add('cpi');
    }
    // RPI is consumed ONLY by an explicit rpiPlus escalation. Nothing else reads
    // it, so an RPI row on an assumptions page with no RPI-linked item is noise.
    if (kinds.has('rpiPlus'))
        consumed.add('rpi');
    // Earnings growth: the default for earned income, `earningsPlus`, and the
    // triple-lock proxy (which takes the max of CPI, earnings and 2.5%).
    const statePensionUsesDefault = streams.some((s) => s.type === 'state_pension' && s.growthRateBps === null);
    if (streams.some((s) => (s.type === 'employment' || s.type === 'self_employment') && s.growthRateBps === null) ||
        kinds.has('earningsPlus') ||
        (statePensionUsesDefault && input.assumptions.statePensionEscalation?.kind !== 'flat')) {
        consumed.add('wageGrowth');
    }
    // The state-pension escalation basis is read only when a state pension is
    // actually being modelled AND it has not been given its own rate. This is
    // what catches a state pension captured as generic "other income": the
    // published triple-lock figure would drive nothing.
    if (statePensionUsesDefault)
        consumed.add('statePensionEscalation');
    // Per-wrapper growth/yield/fee are read for wrappers the plan holds. A DC
    // pension pot may also be built by an income stream drawing on it.
    const heldWrappers = new Set(wrappers);
    if (streams.some((s) => s.type === 'dc_pension'))
        heldWrappers.add('dc_pension');
    for (const w of WRAPPER_VALUES) {
        if (!heldWrappers.has(w))
            continue;
        consumed.add(`growth.${w}`);
        consumed.add(`yield.${w}`);
        consumed.add(`fee.${w}`);
    }
    for (const name of referencedIndices(escalations))
        consumed.add(`customIndex.${name}`);
    return consumed;
}
/**
 * Assumption keys the consumed-check deliberately never questions. Schedule
 * steps are dated, adviser-authored windows whose relevance depends on the
 * projection horizon rather than on the plan's contents, and `cpi` underpins
 * the deflator that produces every today's-value figure even in a plan with
 * nothing else inflation-linked.
 */
function isExemptFromConsumedCheck(key) {
    return key.startsWith('schedule.') || key === 'cpi';
}
/** A per-asset-class rate row (`growth.gia`, `yield.cash`, `fee.property`). */
function isPerWrapperKey(key) {
    return key.startsWith('growth.') || key.startsWith('yield.') || key.startsWith('fee.');
}
// ---- the gate ---------------------------------------------------------------
/**
 * Validate an assembled plan. Blocking findings mean the projection must not
 * run; warnings mean it runs and the reader is told what was assumed.
 *
 * `clock` supplies the as-of date and start year — the engine never reads an
 * ambient clock, so "before the production date" is judged against the date
 * the caller injected.
 */
export function validatePlan(input, clock) {
    const findings = [];
    // ---- blocking 1: income arriving from a balance sheet that never loaded --
    const openingAssetValue = input.assets.reduce((sum, a) => sum + a.currentValue, 0);
    if (openingAssetValue === 0) {
        input.incomeStreams.forEach((stream, index) => {
            if (stream.amount <= 0 || !ASSET_DERIVED_INCOME.has(stream.type))
                return;
            findings.push({
                code: 'income-without-opening-assets',
                severity: 'blocking',
                path: `incomeStreams[${index}].amount`,
                message: 'No opening assets recorded, but income is being received from them. ' +
                    `"${stream.label}" is ${stream.type.replace('_', ' ')} income, which has to arise from an asset the household holds, ` +
                    'and no asset has been captured with an opening value.',
                itemLabel: stream.label,
            });
        });
    }
    // ---- blocking 2: nothing takes anything away ----------------------------
    const hasSpendingItem = input.spending.some((s) => s.amount > 0);
    const hasSpendingEvent = (input.plannedEvents ?? []).some(isSpendingEvent);
    if (!hasSpendingItem && !hasSpendingEvent) {
        findings.push({
            code: 'no-modelled-spending',
            severity: 'blocking',
            path: 'spending',
            message: 'No spending recorded. A plan with no spending cannot be assessed for sustainability.',
            itemLabel: null,
        });
    }
    // ---- blocking 3: a total row entered as an income line ------------------
    // Compared on ANNUALISED amounts so a monthly line cannot masquerade as a
    // total of annual ones.
    const annualStreams = input.incomeStreams.map((s) => ({
        stream: s,
        amount: annualised(s.amount, s.frequency),
    }));
    const totalIncome = annualStreams.reduce((sum, s) => sum + s.amount, 0);
    annualStreams.forEach(({ stream, amount }, index) => {
        if (amount <= 0)
            return;
        const others = annualStreams.filter((_, i) => i !== index).filter((s) => s.amount > 0);
        if (others.length < MIN_STREAMS_BEHIND_A_TOTAL)
            return;
        const sumOfOthers = totalIncome - amount;
        if (sumOfOthers <= 0)
            return;
        const differenceBps = Math.abs((amount - sumOfOthers) * 10_000) / sumOfOthers;
        if (differenceBps > DUPLICATE_TOTAL_TOLERANCE_BPS)
            return;
        findings.push({
            code: 'income-stream-matches-total-of-others',
            severity: 'blocking',
            path: `incomeStreams[${index}].amount`,
            message: `Possible duplicate: "${stream.label}" matches the total of the others. ` +
                'Check for a total row entered as an income line.',
            itemLabel: stream.label,
        });
    });
    // ---- blocking 4: the plan starts before it was produced ------------------
    const asOfTaxYear = taxYearContaining(clock.asOfDate);
    if (clock.startYear < asOfTaxYear) {
        findings.push({
            code: 'start-year-before-as-of-date',
            severity: 'blocking',
            path: 'startYear',
            message: `Plan starts before the production date. The first projected year is ${clock.startYear}/${String((clock.startYear + 1) % 100).padStart(2, '0')}, ` +
                `but ${clock.asOfDate} falls in ${asOfTaxYear}/${String((asOfTaxYear + 1) % 100).padStart(2, '0')}.`,
            itemLabel: null,
        });
    }
    // ---- warning: an asset class modelled on a nil growth rate ---------------
    // A nil rate is a silent wrong answer, not a neutral one: it says the asset
    // never moves, which no adviser would state out loud about a business or a
    // property. Assets carrying their OWN rate are not questioned.
    const nilGrowthWrappers = new Map();
    input.assets.forEach((asset) => {
        if (asset.currentValue <= 0 && asset.contribution <= 0)
            return;
        if (asset.growthRateBps !== null)
            return;
        if (input.assumptions.growthBpsByWrapper[asset.wrapper] !== 0)
            return;
        const labels = nilGrowthWrappers.get(asset.wrapper) ?? [];
        labels.push(asset.label);
        nilGrowthWrappers.set(asset.wrapper, labels);
    });
    for (const [wrapper, labels] of nilGrowthWrappers) {
        findings.push({
            code: 'zero-growth-rate-in-use',
            severity: 'warning',
            path: `assumptions.overrides.growthByWrapper.${wrapper}`,
            message: `${labels.length === 1 ? 'An asset' : `${labels.length} assets`} of type "${wrapperLabel(wrapper)}" ${labels.length === 1 ? 'is' : 'are'} modelled on a growth rate of 0% a year, ` +
                `because no rate has been set for that asset class and none was entered on the ${labels.length === 1 ? 'asset' : 'assets'} (${labels.join(', ')}). ` +
                'The projection therefore holds their value flat for its whole length.',
            itemLabel: labels.join(', '),
        });
    }
    // ---- warning: income rows an adviser cannot tell apart -------------------
    // The engine carries the caller's own label through to the derivation, so an
    // indistinguishable row means an indistinguishable INPUT. An adviser has to
    // be able to answer "which line is the NHS pension?".
    const byLabel = new Map();
    input.incomeStreams.forEach((stream, index) => {
        if (stream.amount <= 0)
            return;
        const key = stream.label.trim().toLowerCase();
        const seen = byLabel.get(key) ?? [];
        seen.push(index);
        byLabel.set(key, seen);
    });
    for (const [key, indices] of byLabel) {
        if (key.length > 0 && indices.length < 2)
            continue;
        const first = indices[0];
        findings.push({
            code: 'income-streams-not-distinctly-labelled',
            severity: 'warning',
            path: `incomeStreams[${first}].label`,
            message: key.length === 0
                ? 'An income stream has no label, so it cannot be identified in the year-by-year derivation.'
                : `${indices.length} income streams share the label "${input.incomeStreams[first].label}", ` +
                    'so they cannot be told apart in the year-by-year derivation.',
            itemLabel: key.length === 0 ? null : input.incomeStreams[first].label,
        });
    }
    // ---- warning: a figure the adviser flagged as an estimate ---------------
    // The builder's own hint promises "the report labels this figure as an
    // estimate rather than presenting it as precise". Honour that: a figure whose
    // basis is unverified qualifies every figure that descends from it, which is
    // exactly what the assumptions page exists to say. Silence here would be the
    // `cgtChattelsExempt` defect again — a flag that reassures and does nothing.
    const unverified = input.assets.filter((a) => a.valueUnverified === true);
    if (unverified.length > 0) {
        findings.push({
            code: 'figure-unverified',
            severity: 'warning',
            path: `assets[${input.assets.findIndex((a) => a.valueUnverified === true)}].currentValue`,
            message: `${unverified.length === 1 ? 'One figure in this plan is' : `${unverified.length} figures in this plan are`} recorded as an estimate rather than a verified value ` +
                `(${unverified.map((a) => a.label).join(', ')}). Every projected figure that follows from ` +
                `${unverified.length === 1 ? 'it' : 'them'} is only as precise as that estimate.`,
            itemLabel: unverified.map((a) => a.label).join(', '),
        });
    }
    // ---- warning: an asset flag that gates a calculation never performed ----
    // `cgtChattelsExempt` marks a holding whose disposal would be free of CGT
    // (private motor cars, wasting chattels). The engine has no disposal path
    // for `other_asset` at all: such holdings grow, count toward net worth and
    // are fully chargeable to IHT, but are never sold. Setting the flag is
    // therefore silently reassuring — it reads as "the exemption is handled"
    // when the sale it would apply to is not modelled in the first place.
    for (const [index, asset] of input.assets.entries()) {
        if (asset.cgtChattelsExempt !== true)
            continue;
        if (asset.wrapper !== 'other_asset')
            continue;
        findings.push({
            code: 'asset-flag-not-consumed',
            severity: 'warning',
            path: `assets[${index}].cgtChattelsExempt`,
            message: `"${asset.label}" is marked exempt from capital gains tax on disposal, but this projection ` +
                'never sells it — holdings of this type are held to the end of the plan, so no gain arises ' +
                'for the exemption to apply to. Its full value is still counted in the estate for inheritance tax.',
            itemLabel: asset.label,
        });
    }
    // ---- warning: a Junior ISA funded twice over ----------------------------
    // A JISA asset carrying a fixed `contribution` (paid every year at step 6)
    // AND a `jisa` sweep step for the same child (paid from surplus at step 10)
    // are both modelled, sharing that child's statutory limit. That is a valid
    // plan — a floor plus a top-up — but it is rarely what a single meeting
    // note meant, so the projection says so rather than quietly paying twice.
    for (const [index, step] of (input.waterfall?.steps ?? []).entries()) {
        if (step.target !== 'jisa')
            continue;
        const fixed = input.assets.filter((a) => a.wrapper === 'jisa' && a.dependentId === step.dependentId && a.contribution > 0);
        if (fixed.length === 0)
            continue;
        const dep = (input.dependents ?? []).find((d) => d.id === step.dependentId);
        findings.push({
            code: 'jisa-double-funded',
            severity: 'warning',
            path: `waterfall.steps[${index}]`,
            message: `${dep?.label ?? 'The child'}'s Junior ISA is funded in two ways in this projection: a fixed ` +
                `contribution to "${fixed.map((a) => a.label).join('", "')}" is paid every year, and the ` +
                'surplus sweep also places money into it in years with room. Both are modelled, within the ' +
                'Junior ISA annual limit for that child.',
            itemLabel: fixed[0]?.label ?? null,
        });
    }
    // ---- warning: an assumption published but never read --------------------
    // Per-wrapper rates are aggregated into ONE finding: the snapshot publishes
    // growth for every wrapper that exists, so a plan holding three asset types
    // would otherwise raise ten near-identical warnings and bury the scalar ones
    // (a state-pension escalation that drove nothing) that actually matter.
    const snapshot = input.assumptionsSnapshot;
    if (snapshot !== undefined) {
        const consumed = consumedAssumptionKeys(input);
        const unconsumedWrapperKeys = [];
        for (const entry of snapshot.entries) {
            if (isExemptFromConsumedCheck(entry.key))
                continue;
            if (consumed.has(entry.key))
                continue;
            if (isPerWrapperKey(entry.key)) {
                unconsumedWrapperKeys.push(entry.key);
                continue;
            }
            findings.push({
                code: 'assumption-not-consumed',
                severity: 'warning',
                path: `assumptions.${entry.key}`,
                message: `The assumptions table publishes "${entry.key}", but nothing in this plan uses it, ` +
                    'so it did not affect any figure in the projection.',
                itemLabel: entry.key,
            });
        }
        if (unconsumedWrapperKeys.length > 0) {
            findings.push({
                code: 'assumption-not-consumed',
                severity: 'warning',
                path: 'assumptions.overrides',
                message: `The assumptions table publishes rates for asset types this plan does not hold (${unconsumedWrapperKeys.join(', ')}), ` +
                    'so none of them affected any figure in the projection.',
                itemLabel: unconsumedWrapperKeys.join(', '),
            });
        }
    }
    return findings;
}
/** The blocking subset — the findings that must stop a projection. */
export function blockingFindings(findings) {
    return findings.filter((f) => f.severity === 'blocking');
}
/**
 * The refusal itself. Call this at every boundary that turns a projection into
 * something a person reads — an API response, a persisted run, a rendered
 * report. Takes the projection RESULT (which carries its own findings) so a
 * caller cannot accidentally validate a different plan from the one it is
 * about to publish.
 *
 * Deliberately NOT called inside `projectHousehold`: a plan being built up in
 * the builder is incomplete by nature and must still preview, but nothing
 * client-facing may be produced from it.
 */
export function assertPlanProjectable(result) {
    const blocking = blockingFindings(result.validation);
    if (blocking.length > 0)
        throw new PlanRefusedError(result.validation);
}
//# sourceMappingURL=plan-validation.js.map